/**
 * Formats an ISO date string to a human-readable date.
 * @example formatDate('2024-01-15T10:30:00Z') → 'Jan 15, 2024'
 */
export const formatDate = (isoString: string): string =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(isoString));

/**
 * Formats an ISO date string to a human-readable date + time.
 * @example formatDateTime('2024-01-15T10:30:00Z') → 'Jan 15, 2024, 10:30 AM'
 */
export const formatDateTime = (isoString: string): string =>
  new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(isoString));

/**
 * Today's date as a local 'YYYY-MM-DD' string (not UTC, so it doesn't shift a
 * day near midnight in non-UTC zones). Useful as a DatePicker max bound.
 */
export const todayIso = (): string => {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Truncates a string to the given max length, appending '…'.
 */
export const truncate = (str: string, maxLength: number): string =>
  str.length <= maxLength ? str : `${str.slice(0, maxLength)}…`;

/**
 * Capitalises the first letter of a string.
 */
export const capitalise = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);
